import React from "react";
import { CheckCircle, AlertTriangle, Building2, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const FeaturesComparisonSection = () => {
  const { user } = useAuth();

  return (
    <section className="py-12 md:py-20 bg-white border-t border-gray-200">
      <div className="container px-4 md:px-6">
        <div className="mb-8 md:mb-16 text-center">
          <h2 className="mb-3 text-2xl md:text-3xl font-bold text-gray-900">
            Compare Okkio Plans
          </h2>
          <p className="mx-auto max-w-2xl text-base md:text-lg text-gray-600">
            Choose the right plan for your safety needs
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-3">
          {/* Citizen Plan */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:shadow-md">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">Citizen</h3>
                <p className="text-sm md:text-base text-gray-500">Basic safety features</p>
              </div>
              <div className="rounded-full bg-gray-100 p-2">
                <Shield className="h-6 w-6 md:h-8 md:w-8 text-gray-600" />
              </div>
            </div>

            <div className="mb-6 md:mb-8">
              <p className="text-2xl md:text-3xl font-bold text-gray-900">Free</p>
              <p className="text-xs md:text-sm text-gray-500">Forever</p>
            </div>

            <div className="mb-6 md:mb-8 space-y-3 md:space-y-4">
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Report crimes in your area</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Visualize crime data on a real-time map</span>
              </div>
              <div className="flex items-start opacity-50">
                <AlertTriangle className="mr-3 h-5 w-5 text-gray-400 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-400">Real-time crime alerts (Vigilante only)</span>
              </div>
              <div className="flex items-start opacity-50">
                <AlertTriangle className="mr-3 h-5 w-5 text-gray-400 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-400">Proximity alerts to other users (Vigilante only)</span>
              </div>
              <div className="flex items-start opacity-50">
                <AlertTriangle className="mr-3 h-5 w-5 text-gray-400 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-400">High crime rate notifications (Vigilante only)</span>
              </div>
            </div>

            {!user ? (
              <Link to="/register" className="w-full">
                <Button className="w-full">Sign Up Free</Button>
              </Link>
            ) : (
              <Button variant="outline" className="w-full" disabled>
                Current Plan
              </Button>
            )}
          </div>

          {/* Vigilante Plan */}
          <div className="rounded-xl border-2 border-okkio-orange bg-white p-6 md:p-8 shadow-md relative">
            <div className="absolute -top-3 md:-top-4 right-4 bg-okkio-orange text-white px-3 py-1 rounded-full text-xs md:text-sm font-bold">
              Premium
            </div>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">Vigilante</h3>
                <p className="text-sm md:text-base text-gray-500">Advanced safety features</p>
              </div>
              <div className="rounded-full bg-okkio-orange/10 p-2">
                <Shield className="h-6 w-6 md:h-8 md:w-8 text-okkio-orange" />
              </div>
            </div>

            <div className="mb-6 md:mb-8">
              <p className="text-2xl md:text-3xl font-bold text-gray-900">4.99€<span className="text-base md:text-lg text-gray-500">/month</span></p>
              <p className="text-xs md:text-sm text-gray-500">Cancel anytime</p>
            </div>

            <div className="mb-6 md:mb-8 space-y-3 md:space-y-4">
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">All Citizen features</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Real-time crime alerts in a 500m radius</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Alerts for high crime rate areas</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Proximity alerts to nearby Okkio users</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Priority access to new safety features</span>
              </div>
            </div>

            <Link to="/upgrade" className="w-full">
              <Button className="w-full bg-okkio-orange hover:bg-okkio-orange-600">
                {user ? "Upgrade Now" : "Sign Up & Upgrade"}
              </Button>
            </Link>
          </div>

          {/* Enterprise Plan */}
          <div className="rounded-xl border-2 border-navy-800 bg-white p-6 md:p-8 shadow-md relative">
            <div className="absolute -top-3 md:-top-4 right-4 bg-navy-800 text-white px-3 py-1 rounded-full text-xs md:text-sm font-bold">
              Enterprise
            </div>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">Enterprise</h3>
                <p className="text-sm md:text-base text-gray-500">Custom security solutions</p>
              </div>
              <div className="rounded-full bg-navy-800/10 p-2">
                <Building2 className="h-6 w-6 md:h-8 md:w-8 text-navy-800" />
              </div>
            </div>

            <div className="mb-6 md:mb-8">
              <p className="text-2xl md:text-3xl font-bold text-gray-900">Custom</p>
              <p className="text-xs md:text-sm text-gray-500">Contact for pricing</p>
            </div>

            <div className="mb-6 md:mb-8 space-y-3 md:space-y-4">
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">All Vigilante features</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Dedicated account manager</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Custom integrations</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Priority 24/7 support</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="mr-3 h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700">Custom security features</span>
              </div>
            </div>

            <Link to="/contact" className="w-full">
              <Button className="w-full bg-black text-white hover:bg-gray-900">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesComparisonSection;
